import { useEffect, useRef, useState } from "react";
import { useParams } from "@tanstack/react-router";
import {
  getUser,
  getUsersApi,
  grantMembership,
  updateUser,
  useActiveHotel,
  useCore,
} from "@praesto/core";
import type { Employment, GovernedBy } from "@praesto/shared-types";
import {
  Input,
  Select,
  Textarea,
  FormField,
  Badge,
  SkeletonFormSection,
  Section,
} from "@praesto/ui-kit";
import { DepartmentMultiSelect } from "./DepartmentMultiSelect";
import { type ProfileField, MANAGER_ROLES, profileEditScope } from "./profile-edit-scope";

interface InfoForm {
  displayName: string;
  phone?: string | undefined;
  birthDate?: string | undefined;
  employment: Employment;
  notes?: string | undefined;
}

// The API returns extra fields beyond UserSummary — widen with this helper type
interface UserDetail {
  displayName: string;
  phone?: string | undefined;
  birthDate?: string | undefined;
  employment?: Employment | undefined;
  notes?: string | undefined;
  // Provenance marker written by inbound HR connectors — present when an
  // external system (e.g. TeamSystem) owns some of this person's fields.
  governedBy?: GovernedBy | undefined;
}

/**
 * The fields of `next` that differ from `saved` and that `mayWrite` lets
 * through. The server refuses the whole PUT when the body names a field it
 * does not let this user write, even one that did not change (C-228) — so an
 * unchanged field is never sent.
 */
function changedFields(
  saved: InfoForm,
  next: InfoForm,
  mayWrite: (field: ProfileField) => boolean,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (mayWrite("displayName") && next.displayName !== saved.displayName) {
    payload["displayName"] = next.displayName;
  }
  // "" means the field was emptied: an explicit null removes the value
  // (C-230, C-233, C-234), while an unchanged field is left out.
  if (mayWrite("phone") && (next.phone ?? "") !== (saved.phone ?? "")) {
    payload["phone"] = next.phone ? next.phone : null;
  }
  if (mayWrite("birthDate") && (next.birthDate ?? "") !== (saved.birthDate ?? "")) {
    payload["birthDate"] = next.birthDate ? next.birthDate : null;
  }
  if (mayWrite("employment") && employmentChanged(saved.employment, next.employment)) {
    payload["employment"] = next.employment;
  }
  if (mayWrite("notes") && (next.notes ?? "") !== (saved.notes ?? "")) {
    payload["notes"] = next.notes ? next.notes : null;
  }
  return payload;
}

function employmentChanged(a: Employment, b: Employment): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof Employment>;
  return [...keys].some((k) => a[k] !== b[k]);
}

export function ProfileInfoTab() {
  const core = useCore();
  const { uid } = useParams({ strict: false }) as { uid: string };
  const { reachable: hotels } = useActiveHotel();
  const [form, setForm] = useState<InfoForm | null>(null);
  const [governedBy, setGovernedBy] = useState<GovernedBy | undefined>(undefined);
  // The person's hotel memberships, each carrying the departments they work in.
  // Kept in state (not just rendered from the fetch) because editing departments
  // writes through `grantMembership` and must show the new value immediately.
  const [memberships, setMemberships] = useState<ReadonlyArray<MembershipRow>>([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // What the server holds as far as this tab knows: what was loaded, then what
  // the last successful save wrote. A save sends only what differs from it.
  const saved = useRef<InfoForm | null>(null);

  const identityGoverned = !!governedBy?.capabilities.includes("identity");
  const employmentGoverned = !!governedBy?.capabilities.includes("employment");
  // Same gate as the "Modifica" action in PeopleList: assigning someone to a
  // department is a management action, not something a person does to themselves.
  const canManageUsers = core.rbac.can("manage", "user");
  // `manage:hotel` is the owner's alone (same test as OnboardingGuard).
  const viewerIsOwner = core.rbac.can("manage", "hotel");
  const scope = profileEditScope({
    viewerIsOwner,
    isSelf: uid === core.auth.currentUserId(),
    memberships,
  });
  // POST /:uid/memberships also refuses a hotel where the viewer is only staff
  // (C-229): a manager looking at their own profile sees those departments, but
  // cannot change them. Same manager roles as the server's managedHotelIds.
  const canEditDepartmentsAt = (hotelId: string): boolean =>
    scope.reason !== "owner_target" &&
    (viewerIsOwner || MANAGER_ROLES.has(core.rbac.roleAt(hotelId) ?? ""));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const result = await getUser(api.apiBase, uid, tok);
      const u = result.user as UserSummaryExtended;
      if (!cancelled) {
        const loaded: InfoForm = {
          displayName: u.displayName ?? "",
          phone: u.phone,
          birthDate: u.birthDate,
          employment: u.employment ?? {},
          notes: u.notes,
        };
        saved.current = loaded;
        setForm(loaded);
        setGovernedBy(u.governedBy);
        setMemberships(result.memberships as ReadonlyArray<MembershipRow>);
      }
    })()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  function debouncedSave(patch: Partial<InfoForm>) {
    if (!form) return;
    // Defense-in-depth: the server already 403s a write to a governed field,
    // but a disabled input firing a stray onChange shouldn't spam that call.
    const touchesIdentity = "displayName" in patch || "phone" in patch || "birthDate" in patch;
    const touchesEmployment = "employment" in patch;
    if ((touchesIdentity && identityGoverned) || (touchesEmployment && employmentGoverned)) return;
    // Same for a field PUT /:uid refuses to this user (C-174, C-175).
    if ((Object.keys(patch) as ProfileField[]).some((f) => !scope.canEdit(f))) return;
    const next = { ...form, ...patch };
    setForm(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void (async () => {
        try {
          // Only the fields that changed and that this save may carry: the
          // server refuses the whole PUT when the body names a field that is
          // not the user's or that an external system governs, even unchanged.
          const payload = changedFields(saved.current ?? next, next, (field) => {
            if (!scope.canEdit(field)) return false;
            if (field === "employment") return !employmentGoverned;
            return field === "notes" || !identityGoverned;
          });
          if (Object.keys(payload).length === 0) return;
          setSaveStatus("saving");
          const api = getUsersApi();
          const tok = await api.getAuthToken();
          await updateUser(api.apiBase, uid, payload as Parameters<typeof updateUser>[2], tok);
          saved.current = next;
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 1500);
        } catch {
          setSaveStatus("error");
        }
      })();
    }, 500);
  }

  /**
   * `POST /:uid/memberships` upserts the WHOLE membership, so the current role
   * has to be echoed back verbatim. Defaulting it here would silently re-grade
   * the person — and on a `tenant_owner` a department edit would become a
   * privilege change. The server validates that every code exists in that
   * hotel (`firstInvalidDepartment`) and writes the audit entry.
   */
  function saveDepartments(membership: MembershipRow, departments: ReadonlyArray<string>): void {
    // Defense-in-depth, as in debouncedSave: a disabled box firing a stray
    // onChange must not turn into a request the server will refuse (C-229).
    if (!canEditDepartmentsAt(membership.hotelId)) return;
    // Captured before the optimistic flip, so a failed save can put the row
    // back exactly as it was. Leaving the ticked box on a failure is not
    // cosmetic: departments gate which shifts a person can be assigned to, so
    // a manager trusting the checkbox would roster them into a department the
    // server never granted.
    const previous = membership.departments;
    setMemberships((prev) => prev.map((m) => (m.id === membership.id ? { ...m, departments } : m)));
    void (async () => {
      setSaveStatus("saving");
      try {
        const api = getUsersApi();
        const tok = await api.getAuthToken();
        await grantMembership(
          api.apiBase,
          uid,
          { hotelId: membership.hotelId, role: membership.role, departments: [...departments] },
          tok,
        );
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 1500);
      } catch {
        setMemberships((prev) =>
          prev.map((m) => (m.id === membership.id ? { ...m, departments: previous } : m)),
        );
        setSaveStatus("error");
      }
    })();
  }

  if (loading) return <SkeletonFormSection fields={6} />;
  if (!form) return <p className="text-sm text-danger">Errore nel caricamento.</p>;

  return (
    <div className="space-y-6">
      <div className="h-6 flex items-center">
        {saveStatus === "saving" && (
          <Badge tone="neutral" variant="soft" size="xs">
            Salvataggio…
          </Badge>
        )}
        {saveStatus === "saved" && (
          <Badge tone="success" variant="soft" size="xs">
            Salvato
          </Badge>
        )}
        {saveStatus === "error" && (
          <Badge tone="danger" variant="soft" size="xs">
            Errore nel salvataggio
          </Badge>
        )}
      </div>

      {scope.reason && (
        <p className="text-caption text-fg-muted">
          {core.i18n.t(`people.profile.readOnly.${scope.reason}`)}
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
        <section className="space-y-3">
          <div className="text-eyebrow flex items-center gap-2">
            Anagrafica
            {identityGoverned && (
              <Badge tone="info" variant="soft" size="xs">
                {core.i18n.t("people.governedBy", { provider: governedBy?.provider ?? "" })}
              </Badge>
            )}
          </div>
          <FormField label="Nome">
            <Input
              disabled={identityGoverned || !scope.canEdit("displayName")}
              value={form.displayName}
              onChange={(e) => debouncedSave({ displayName: e.target.value })}
            />
          </FormField>
          <FormField label="Telefono">
            <Input
              disabled={identityGoverned || !scope.canEdit("phone")}
              placeholder="+39..."
              value={form.phone ?? ""}
              onChange={(e) => debouncedSave({ phone: e.target.value })}
            />
          </FormField>
          <FormField label="Data di nascita">
            <Input
              disabled={identityGoverned || !scope.canEdit("birthDate")}
              type="date"
              value={form.birthDate ?? ""}
              onChange={(e) => debouncedSave({ birthDate: e.target.value })}
            />
          </FormField>
        </section>

        <section className="space-y-3">
          <div className="text-eyebrow flex items-center gap-2">
            Contratto
            {employmentGoverned && (
              <Badge tone="info" variant="soft" size="xs">
                {core.i18n.t("people.governedBy", { provider: governedBy?.provider ?? "" })}
              </Badge>
            )}
          </div>
          <FormField label="Livello contrattuale">
            <Input
              disabled={employmentGoverned || !scope.canEdit("employment")}
              value={form.employment.ccnlLevel ?? ""}
              onChange={(e) =>
                debouncedSave({
                  employment: { ...form.employment, ccnlLevel: e.target.value || undefined },
                })
              }
            />
          </FormField>
          <FormField label="Tipo contratto">
            <Select
              disabled={employmentGoverned || !scope.canEdit("employment")}
              value={form.employment.contractType ?? ""}
              onChange={(e) =>
                debouncedSave({
                  employment: {
                    ...form.employment,
                    contractType: (e.target.value || undefined) as Employment["contractType"],
                  },
                })
              }
            >
              <option value="">—</option>
              <option value="permanent">Indeterminato</option>
              <option value="fixed_term">Determinato</option>
              <option value="apprentice">Apprendista</option>
              <option value="seasonal">Stagionale</option>
              <option value="intern">Tirocinio</option>
            </Select>
          </FormField>
          <FormField label="Ore settimanali">
            <Input
              disabled={employmentGoverned || !scope.canEdit("employment")}
              type="number"
              min={1}
              max={80}
              value={form.employment.hoursPerWeek ?? ""}
              onChange={(e) => {
                const v = e.target.value ? parseInt(e.target.value, 10) : undefined;
                debouncedSave({ employment: { ...form.employment, hoursPerWeek: v } });
              }}
            />
          </FormField>
          <FormField label="Data assunzione">
            <Input
              disabled={employmentGoverned || !scope.canEdit("employment")}
              type="date"
              value={form.employment.startDate ?? ""}
              onChange={(e) =>
                debouncedSave({
                  employment: { ...form.employment, startDate: e.target.value || undefined },
                })
              }
            />
          </FormField>
        </section>

        {canManageUsers && (
          // Spans the full row until there's a third column to sit in: at the
          // 2-column breakpoint a lone third grid item would otherwise land in
          // row 2 sharing half the width, too cramped for a row of department
          // checkboxes (DepartmentMultiSelect wraps but still needs room).
          <section className="space-y-4 sm:col-span-2 xl:col-span-1">
            <div className="text-eyebrow">Strutture e reparti</div>
            {memberships.length === 0 ? (
              <p className="text-caption text-fg-muted">Nessuna struttura assegnata.</p>
            ) : (
              memberships.map((m) => {
                const editable = canEditDepartmentsAt(m.hotelId);
                return (
                  <div key={m.id} className="space-y-2">
                    <div className="text-sm font-medium text-fg-default">
                      {hotels.find((h) => h.id === m.hotelId)?.name ?? m.hotelId}
                    </div>
                    <DepartmentMultiSelect
                      hotelId={m.hotelId}
                      // POST /:uid/memberships refuses a manager on an owner (C-166)
                      // and a hotel the viewer does not manage (C-229).
                      disabled={!editable}
                      value={m.departments}
                      onChange={(codes) => saveDepartments(m, codes)}
                    />
                    {!editable && scope.reason !== "owner_target" && (
                      <p className="text-caption text-fg-muted">
                        {core.i18n.t("people.profile.readOnly.departments_not_managed")}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </section>
        )}
      </div>

      <Section width="readable" className="space-y-2">
        <div className="text-eyebrow">Note manager</div>
        <Textarea
          disabled={!scope.canEdit("notes")}
          rows={6}
          maxLength={2000}
          value={form.notes ?? ""}
          onChange={(e) => debouncedSave({ notes: e.target.value })}
        />
        <div className="whitespace-nowrap text-mono-xs text-fg-muted">
          {(form.notes ?? "").length}/2000
        </div>
      </Section>
    </div>
  );
}

/** One row of `GET /api/users/:uid`'s `memberships` — the person's assignment
 *  to a hotel, with the departments they work in there. */
interface MembershipRow {
  readonly id: string;
  readonly hotelId: string;
  readonly role: "tenant_owner" | "manager_hotel" | "manager_functional" | "staff";
  readonly departments: ReadonlyArray<string>;
}

// The API response carries fields beyond the typed UserSummary; use a local widening.
interface UserSummaryExtended extends UserDetail {
  id: string;
  email: string;
  displayName: string;
  status: "pending" | "active" | "disabled" | "archived";
}
